import { useRouter } from "next/compat/router";
import * as qs from "qs";
import { useMemo } from "react";

export const useQueryString = (): qs.ParsedQs => {
  const { asPath = "" } = useRouter() || {};

  const query = useMemo(() => {
    if (!asPath) {
      return {};
    }

    return qs.parse(asPath.split(/\?/)[1], { parseArrays: false });
  }, [asPath]);

  return query;
};

export const useRouterQueryParameter = (queryParameterName: string): string => {
  const { query = {} } = useRouter() || {};

  const queryParameterValue = query[queryParameterName];
  if (Array.isArray(queryParameterValue)) {
    throw new Error(
      `The query parameter "${queryParameterName}" param should be unique in the query string: it could not be used multiple time as an array of query params.`,
    );
  }

  if (queryParameterValue === undefined) {
    return "";
  }

  return queryParameterValue;
};

export const useRouterQueryParameterMap = (
  queryParameterName: string,
): Record<string, string> => {
  const query = useQueryString();

  // @ts-expect-error: The returned QsQuery from qs.parse is really complex to convert. As we check it is safe to ignore the TS error.
  const record: Record<string, string> = useMemo(() => {
    const queryParameterRecord = query[queryParameterName];
    if (!queryParameterRecord) {
      return {};
    }

    if (typeof queryParameterRecord !== "object") {
      throw new Error(
        `The record query parameter "${queryParameterName}" should be a map not a primitive.`,
      );
    }

    if (Array.isArray(queryParameterRecord)) {
      throw new Error(
        `The record query parameter "${queryParameterName}" should be a map: not an array of value.`,
      );
    }

    Object.values(queryParameterRecord).forEach((oneRecordValue) => {
      if (Array.isArray(oneRecordValue)) {
        throw new Error(
          `The record query parameter "${queryParameterName}" contain deep record: only one level is allowed.`,
        );
      }
    });

    return queryParameterRecord;
  }, [queryParameterName, query]);

  return record;
};

export const useRouterQueryStringParameterQuery = (
  queryStringParameterName: string,
):
  | { isReady: false; value: undefined }
  | { isReady: true; value: string | undefined } => {
  const { isReady, query = {} } = useRouter() || {};

  if (!isReady) {
    return { isReady: false, value: undefined };
  }

  const queryStringParameterValue = query[queryStringParameterName];
  if (Array.isArray(queryStringParameterValue)) {
    throw new Error(
      `The query parameter "${queryStringParameterName}" param should be unique in the query string: it could not be used multiple time as an array of query params.`,
    );
  }

  return { isReady: true, value: queryStringParameterValue };
};
